import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getWorkflowAuditTrail } from '@/api/workflowLineage';
import { downloadCSV } from '@/components/Settings/WorkspaceSettings/utils';
import { AUDIT_TRAIL_EXPORT_EVENT } from '@/consts/analytics.consts';
import { useAnalytics } from '@/hooks/useAnalytics';
import { I18N_KEYS } from '@/language/keys';
import { log } from '@/logger/logger';
import { useGlobalStore } from '@/state/global.state';
import type { AuditTrailExportActor, AuditTrailExportScope, AuditTrailExportSurface } from '@/consts/analytics.consts';

const logger = log.getLogger('useExportWorkflowLineage');

const SCOPE: AuditTrailExportScope = 'workflow';

export interface ExportWorkflowLineageTarget {
  workflowId: string;
  workflowName: string;
  actorRole?: AuditTrailExportActor;
}

export function useExportWorkflowLineage(
  surface: AuditTrailExportSurface,
): (target: ExportWorkflowLineageTarget) => Promise<void> {
  const { updateSnackbarData } = useGlobalStore();
  const { track } = useAnalytics();
  const { t } = useTranslation();

  return useCallback(
    async ({ workflowId, workflowName, actorRole }: ExportWorkflowLineageTarget) => {
      try {
        const { csv, fileName, rowCount } = await getWorkflowAuditTrail(workflowId);
        downloadCSV(csv, fileName ?? fallbackFileName(workflowName));
        track(AUDIT_TRAIL_EXPORT_EVENT, {
          ...(rowCount == null ? {} : { row_count: rowCount }),
          ...(actorRole == null ? {} : { actor_role: actorRole }),
          workflow_id: workflowId,
          surface,
          scope: SCOPE,
        });
      } catch (error) {
        logger.error('Failed to export workflow audit trail', error);
        updateSnackbarData({
          text: t(I18N_KEYS.RECIPE_MAIN.ERRORS.EXPORT_AUDIT_TRAIL_FAILED),
          isOpen: true,
          icon: null,
          severity: 'error',
        });
      }
    },
    [surface, track, updateSnackbarData, t],
  );
}

/**
 * Only used when the server's `Content-Disposition` is missing; mirrors the server's shape
 * `{workflowName}-audit-trail-{YYYYMMDDTHHMMSSZ}.csv`.
 */
function fallbackFileName(workflowName: string): string {
  const stamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d+/, '');
  const trimmed = typeof workflowName === 'string' ? workflowName.trim() : '';
  return `${trimmed === '' ? 'workflow' : trimmed}-audit-trail-${stamp}.csv`;
}
